import { useState, useCallback } from 'react';

export interface UserError {
  message: string;
  errorCode?: string;
  statusCode?: number;
  details?: string;
  requestId?: string;
  context?: Record<string, any>;
}

export function useErrorHandler() {
  const [error, setError] = useState<UserError | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  const showError = useCallback((error: Error | UserError | string) => {
    let userError: UserError;

    if (typeof error === 'string') {
      userError = { message: error };
    } else if (error instanceof Error) {
      // Check if it's our enhanced error from API client
      const enhancedError = error as any;
      userError = {
        message: enhancedError.message || 'An unexpected error occurred',
        errorCode: enhancedError.name !== 'Error' ? enhancedError.name : enhancedError.errorCode,
        statusCode: enhancedError.statusCode,
        details: enhancedError.details,
        requestId: enhancedError.requestId,
        context: enhancedError.context
      };
    } else {
      userError = error;
    }

    setError(userError);
    setIsVisible(true);
  }, []);

  const hideError = useCallback(() => {
    setIsVisible(false);
    // Clear error after animation completes
    setTimeout(() => setError(null), 300);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
    setIsVisible(false);
  }, []);

  return {
    error,
    isVisible,
    showError,
    hideError,
    clearError
  };
}